// Home del negozio: apertura, punti di forza, creazioni in evidenza, categorie,
// storia della bottega, scorci del laboratorio, recensioni e invito al su misura.
// Otto aperture diverse e scelte di impaginazione lette dal modello grafico (style).
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, ChevronDown, Hand, Quote, ShieldCheck, Sparkles, Star, Gem } from 'lucide-react';
import { optimizeImageUrl } from '../../utils/imageUrl';
import { getChildCategoryName } from '../../utils/categoryPath';
import { useShop } from '../ShopContext';
import ShopProductCard, { productImage } from '../ShopProductCard';
import { GhostLink, PrimaryButton, Reveal, Section } from '../ShopUi';
import ShopVideo from '../ShopVideo';
import ShopLocation from '../ShopLocation';
import { CollectionTile } from './ShopCollections';
import { parseVideo } from '../../utils/video';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: 'easeOut', delay },
});

const categoryOf = (p) => {
  const path = p.customization_options?.categoryPath;
  return getChildCategoryName(path) || path?.sub || p.category || 'Altro';
};

const shortName = (full) => {
  if (!full) return 'Cliente Rarifex';
  const [first, last] = full.trim().split(/\s+/);
  return last ? `${first} ${last[0]}.` : first;
};

const img = (src, w) => (src ? optimizeImageUrl(src, w) : null);

export default function ShopHome() {
  const shop = useShop();
  const navigate = useNavigate();
  const { crafter, name, style, featured, products, portfolio, reviews, paths, menuKeys, reviewStats, openRequest, collections } = shop;
  const homeCollections = menuKeys.includes('collezioni') ? collections : [];
  const photos = [crafter.cover_image, ...(crafter.cover_images || [])].filter((v, i, a) => v && a.indexOf(v) === i);
  const cover = photos[0];
  // Nel modello Catalogo la vetrina della home mostra molte creazioni (prima quelle in evidenza)
  const showcase = (style.productsFirst
    ? [...featured, ...products.filter((p) => !featured.includes(p))]
    : (featured.length ? featured : products)).slice(0, style.showcase);
  const hasCustom = menuKeys.includes('su_misura');
  const hasAbout = menuKeys.includes('laboratorio');
  const centered = style.centered;
  const place = crafter.city ? `Bottega artigiana · ${crafter.city}` : 'Bottega artigiana';

  // Categorie con immagine (prima creazione di ciascuna)
  const categories = [];
  products.forEach((p) => {
    const c = categoryOf(p);
    if (!categories.find((x) => x.name === c)) categories.push({ name: c, product: p, count: 0 });
    categories.find((x) => x.name === c).count += 1;
  });

  const quotes = (reviews || []).filter((r) => r.comment && r.comment.trim().length > 20 && r.rating >= 4).slice(0, 3);
  const labPhotos = [...photos.slice(1), ...portfolio.map((p) => p.image_url)].filter(Boolean).slice(0, 6);

  const values = [
    { icon: Hand, title: 'Fatto a mano', text: crafter.city ? `Nel laboratorio di ${crafter.city}` : 'Ogni pezzo nasce in bottega' },
    crafter.founded_year
      ? { icon: Calendar, title: `Dal ${crafter.founded_year}`, text: 'Esperienza e passione' }
      : { icon: Gem, title: 'Pezzi originali', text: 'Creazioni non in serie' },
    hasCustom
      ? { icon: Sparkles, title: 'Anche su misura', text: 'Raccontaci la tua idea' }
      : { icon: Star, title: 'Cura dei dettagli', text: 'Materiali scelti con attenzione' },
    { icon: ShieldCheck, title: 'Acquisto protetto', text: 'Pagamento sicuro con Rarifex' },
  ];

  const ctas = (light, extra = '') => (
    <div className={`flex flex-wrap gap-3 ${centered ? 'justify-center' : ''} ${extra}`}>
      <PrimaryButton onClick={() => navigate(paths.page('prodotti'))} className={light ? 'bg-white !text-slate-900 hover:bg-white/90' : ''} data-testid="shop-home-products-cta">
        Scopri le creazioni <ArrowRight className="w-4 h-4" />
      </PrimaryButton>
      {hasCustom && (
        <button
          type="button"
          onClick={openRequest}
          className={`inline-flex items-center gap-2 px-6 py-3 ${style.btn} border font-medium transition-colors ${light ? 'border-white/70 text-white hover:bg-white/10 backdrop-blur-sm' : 'border-shop-primary text-shop-primary hover:bg-shop-primary/5'}`}
          data-testid="shop-home-custom-cta"
        >
          <Sparkles className="w-4 h-4" /> Richiedi su misura
        </button>
      )}
    </div>
  );

  const scrollCue = (
    <motion.button
      type="button"
      onClick={() => window.scrollTo({ top: window.innerHeight * 0.85, behavior: 'smooth' })}
      className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/70 hover:text-white"
      animate={{ y: [0, 6, 0] }}
      transition={{ duration: 2, repeat: Infinity }}
      aria-label="Scorri"
    >
      <ChevronDown className="w-6 h-6" />
    </motion.button>
  );

  const heroes = {
    // Classico: copertina a tutto schermo, titolo serif in basso a sinistra
    cover: (
      <section className="relative h-[88vh] min-h-[520px] max-h-[860px] bg-gradient-to-br from-shop-primary to-shop-primary-dark overflow-hidden">
        {cover && <motion.img initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease: 'easeOut' }} src={img(cover, 2000)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/40" />
        <div className="relative h-full max-w-6xl mx-auto px-4 flex flex-col justify-end pb-16 md:pb-24 text-white">
          <motion.p {...fadeUp(0.1)} className="text-xs md:text-sm uppercase tracking-[0.3em] text-white/80">{place}</motion.p>
          <motion.h1 {...fadeUp(0.2)} className={`${style.display} mt-4 max-w-3xl leading-tight`}>{crafter.storefront_motto || name}</motion.h1>
          {crafter.storefront_motto && <motion.p {...fadeUp(0.3)} className="mt-4 text-lg text-white/85">{name}</motion.p>}
          <motion.div {...fadeUp(0.4)} className="mt-9">{ctas(true)}</motion.div>
        </div>
        {scrollCue}
      </section>
    ),
    // Galleria: testo a sinistra, collage di foto a destra
    collage: (
      <section className="max-w-6xl mx-auto px-4 pt-8 md:pt-12 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        <div className="order-2 md:order-1">
          <motion.p {...fadeUp(0)} className="text-sm uppercase tracking-[0.2em] text-shop-primary font-semibold">{crafter.city || 'Made in Italy'}</motion.p>
          <motion.h1 {...fadeUp(0.1)} className={`${style.display} mt-3 text-slate-900`}>{name}</motion.h1>
          {crafter.storefront_motto && <motion.p {...fadeUp(0.2)} className="mt-5 text-lg md:text-xl text-slate-600 max-w-md">{crafter.storefront_motto}</motion.p>}
          <motion.div {...fadeUp(0.3)} className="mt-8">{ctas(false)}</motion.div>
        </div>
        <motion.div {...fadeUp(0.1)} className="order-1 md:order-2 grid grid-cols-5 grid-rows-5 gap-3 aspect-[4/5] md:aspect-square">
          <div className="col-span-5 row-span-3 md:col-span-3 md:row-span-5 rounded-2xl overflow-hidden bg-shop-primary/10">
            {cover && <img src={img(cover, 1200)} alt="" className="w-full h-full object-cover" />}
          </div>
          {(photos[1] || showcase[0]) && (
            <div className="col-span-3 row-span-2 md:col-span-2 md:row-span-3 rounded-2xl overflow-hidden bg-slate-100">
              <img src={photos[1] ? img(photos[1], 700) : productImage(showcase[0], 700)} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          {(photos[2] || showcase[1]) && (
            <div className="col-span-2 row-span-2 rounded-2xl overflow-hidden bg-slate-100">
              <img src={photos[2] ? img(photos[2], 600) : productImage(showcase[1], 600)} alt="" className="w-full h-full object-cover" />
            </div>
          )}
        </motion.div>
      </section>
    ),
    // Minimale: solo tipografia, centrata
    centered: (
      <section className="max-w-3xl mx-auto px-4 pt-20 pb-14 md:pt-28 md:pb-20 text-center">
        <motion.p {...fadeUp(0)} className="text-xs uppercase tracking-[0.3em] text-slate-400">{place}</motion.p>
        <motion.h1 {...fadeUp(0.1)} className={`${style.display} mt-6 text-slate-900`}>{name}</motion.h1>
        <motion.div {...fadeUp(0.15)} className="w-12 h-px bg-shop-primary mx-auto mt-8" />
        {crafter.storefront_motto && <motion.p {...fadeUp(0.2)} className="mt-8 text-slate-500 text-lg leading-relaxed">{crafter.storefront_motto}</motion.p>}
        <motion.div {...fadeUp(0.3)} className="mt-10">{ctas(false)}</motion.div>
      </section>
    ),
    // Editoriale: testata da rivista, titolo corsivo enorme, foto con didascalia sfalsata
    editorial: (
      <section className="max-w-6xl mx-auto px-4 pt-10 md:pt-14">
        <motion.div {...fadeUp(0)} className="flex items-center justify-between border-b border-slate-900 pb-3 text-xs uppercase tracking-[0.25em] text-slate-600">
          <span>N° {String(new Date().getMonth() + 1).padStart(2, '0')} · {new Date().getFullYear()}</span>
          <span>{crafter.city || 'Made in Italy'}</span>
        </motion.div>
        <div className="grid md:grid-cols-12 gap-8 md:gap-10 pt-10 items-end">
          <div className="md:col-span-7">
            <motion.h1 {...fadeUp(0.1)} className={`${style.display} text-slate-900`}>{name}</motion.h1>
            {crafter.storefront_motto && (
              <motion.p {...fadeUp(0.2)} className="mt-8 text-xl md:text-2xl text-slate-600 leading-relaxed max-w-xl first-letter:text-6xl first-letter:font-['Fraunces',Georgia,serif] first-letter:float-left first-letter:mr-2 first-letter:leading-[0.8] first-letter:text-shop-primary">
                {crafter.storefront_motto}
              </motion.p>
            )}
            <motion.div {...fadeUp(0.3)} className="mt-10">{ctas(false)}</motion.div>
          </div>
          <motion.figure {...fadeUp(0.15)} className="md:col-span-5">
            <div className="aspect-[3/4] overflow-hidden bg-slate-100">
              {cover && <img src={img(cover, 1100)} alt="" className="w-full h-full object-cover" />}
            </div>
            <figcaption className="mt-3 text-xs text-slate-500 italic">— Nella bottega di {crafter.full_name || name}</figcaption>
          </motion.figure>
        </div>
      </section>
    ),
    // Contemporaneo: blocco di colore con nome gigante in maiuscolo, foto a fascia
    block: (
      <section>
        <div className="bg-shop-primary text-white">
          <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
            <motion.p {...fadeUp(0)} className="inline-block bg-white text-slate-900 px-2 py-1 text-xs font-bold uppercase tracking-wider">{place}</motion.p>
            <motion.h1 {...fadeUp(0.1)} className={`${style.display} mt-6 break-words`}>{name}</motion.h1>
            <div className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
              {crafter.storefront_motto && <motion.p {...fadeUp(0.2)} className="text-lg md:text-xl text-white/90 max-w-xl">{crafter.storefront_motto}</motion.p>}
              <motion.div {...fadeUp(0.3)}>{ctas(true)}</motion.div>
            </div>
          </div>
        </div>
        {(cover || showcase.length > 0) && (
          <div className="grid grid-cols-3 border-y-2 border-slate-900">
            {[cover ? img(cover, 900) : null, img(photos[1], 900) || productImage(showcase[0], 900), img(photos[2], 900) || productImage(showcase[1], 900)].map((src, i) => (
              <div key={i} className={`aspect-[4/3] bg-slate-200 overflow-hidden ${i < 2 ? 'border-r-2 border-slate-900' : ''}`}>
                {src && <img src={src} alt="" className="w-full h-full object-cover grayscale-[20%] hover:grayscale-0 transition" />}
              </div>
            ))}
          </div>
        )}
      </section>
    ),
    // Boutique: foto scura a tutto schermo, nome centrato sottile, filetto dorato
    boutique: (
      <section className="relative h-[92vh] min-h-[560px] max-h-[900px] bg-slate-900 overflow-hidden">
        {cover && <motion.img initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.8, ease: 'easeOut' }} src={img(cover, 2000)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative h-full max-w-3xl mx-auto px-4 flex flex-col items-center justify-center text-center text-white">
          <motion.p {...fadeUp(0.2)} className="text-xs uppercase tracking-[0.4em] text-white/80">{place}</motion.p>
          <motion.div {...fadeUp(0.25)} className="w-16 h-px bg-shop-accent mt-6" />
          <motion.h1 {...fadeUp(0.3)} className={`${style.display} mt-6`}>{name}</motion.h1>
          {crafter.storefront_motto && <motion.p {...fadeUp(0.4)} className="mt-6 text-lg md:text-xl italic text-white/85 font-['Cormorant_Garamond',Georgia,serif]">{crafter.storefront_motto}</motion.p>}
          <motion.div {...fadeUp(0.5)} className="w-16 h-px bg-shop-accent mt-8" />
          <motion.div {...fadeUp(0.55)} className="mt-8">{ctas(true)}</motion.div>
        </div>
        {scrollCue}
      </section>
    ),
    // Catalogo: fascia compatta con copertina sottile; le creazioni seguono subito
    compact: (
      <section className="max-w-6xl mx-auto px-4 pt-6">
        <div className="relative h-36 md:h-44 rounded-xl overflow-hidden bg-gradient-to-r from-shop-primary to-shop-primary-dark">
          {cover && <img src={img(cover, 1600)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />}
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
          <div className="relative h-full px-6 flex flex-col justify-center text-white">
            <motion.h1 {...fadeUp(0)} className={`${style.display}`}>{name}</motion.h1>
            {crafter.storefront_motto && <motion.p {...fadeUp(0.1)} className="mt-1 text-white/85 line-clamp-1">{crafter.storefront_motto}</motion.p>}
          </div>
        </div>
        {categories.length > 1 && (
          <div className="mt-4 flex gap-2 overflow-x-auto scrollbar-hide">
            <Link to={paths.page('prodotti')} className="shrink-0 h-9 px-4 inline-flex items-center rounded-full bg-shop-primary text-white text-sm font-medium">Tutte ({products.length})</Link>
            {categories.map((c) => (
              <Link key={c.name} to={`${paths.page('prodotti')}?categoria=${encodeURIComponent(c.name)}`} className="shrink-0 h-9 px-4 inline-flex items-center rounded-full bg-white border border-slate-200 text-sm text-slate-700 hover:border-shop-primary hover:text-shop-primary">
                {c.name} <span className="ml-1.5 text-xs text-slate-400">{c.count}</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    ),
    // Naturale: foto a forma organica, toni morbidi, pulsanti arrotondati
    organic: (
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-shop-primary/10 blur-2xl" aria-hidden />
        <div className="absolute top-40 -left-24 w-72 h-72 rounded-full bg-shop-accent/15 blur-2xl" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-4 pt-10 md:pt-16 pb-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <motion.span {...fadeUp(0)} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white shadow-sm text-sm text-shop-primary font-semibold">
              <Hand className="w-4 h-4" /> {crafter.city ? `Fatto a mano a ${crafter.city}` : 'Fatto a mano'}
            </motion.span>
            <motion.h1 {...fadeUp(0.1)} className={`${style.display} mt-5 text-slate-900`}>{name}</motion.h1>
            {crafter.storefront_motto && <motion.p {...fadeUp(0.2)} className="mt-5 text-lg text-slate-600 max-w-md">{crafter.storefront_motto}</motion.p>}
            <motion.div {...fadeUp(0.3)} className="mt-8">{ctas(false)}</motion.div>
          </div>
          <motion.div {...fadeUp(0.15)} className="relative">
            <div className="aspect-square overflow-hidden bg-shop-primary/10 shadow-xl" style={{ borderRadius: '46% 54% 58% 42% / 48% 42% 58% 52%' }}>
              {cover && <img src={img(cover, 1100)} alt="" className="w-full h-full object-cover" />}
            </div>
            {(photos[1] || showcase[0]) && (
              <div className="absolute -bottom-4 -left-4 w-1/3 aspect-square overflow-hidden rounded-full border-[6px] border-shop-bg shadow-lg">
                <img src={photos[1] ? img(photos[1], 500) : productImage(showcase[0], 500)} alt="" className="w-full h-full object-cover" />
              </div>
            )}
          </motion.div>
        </div>
      </section>
    ),
  };

  const hero = heroes[style.hero] || heroes.cover;

  const showcaseSection = showcase.length > 0 && (
    <Section
      title={style.productsFirst ? 'Le creazioni' : (featured.length ? 'In evidenza' : 'Le ultime creazioni')}
      action={<Link to={paths.page('prodotti')}><GhostLink>Tutte le creazioni <ArrowRight className="w-4 h-4" /></GhostLink></Link>}
      className={style.productsFirst ? '!pt-8' : ''}
    >
      <Reveal>
        <div className={style.grid}>
          {showcase.map((p) => <ShopProductCard key={p.id} product={p} />)}
        </div>
      </Reveal>
    </Section>
  );

  const valuesSection = (
    <section className={`${style.hero === 'collage' || style.hero === 'organic' ? 'mt-10' : ''} ${style.id === 'contemporaneo' ? 'border-b-2 border-slate-900' : 'border-y border-slate-200/80'} ${style.band}`} data-testid="shop-values">
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {values.map((v, i) => (
          <Reveal key={v.title} delay={i * 0.08} className={`flex items-center gap-3 ${centered ? 'flex-col text-center' : ''}`}>
            <span className={`w-10 h-10 ${style.btn === 'rounded-none' ? 'rounded-none' : 'rounded-full'} bg-shop-primary/10 text-shop-primary flex items-center justify-center shrink-0`}>
              <v.icon className="w-5 h-5" />
            </span>
            <span>
              <span className={`block text-sm font-semibold text-slate-900 ${centered ? 'uppercase tracking-[0.12em] text-xs' : ''}`}>{v.title}</span>
              <span className="block text-xs text-slate-500">{v.text}</span>
            </span>
          </Reveal>
        ))}
      </div>
    </section>
  );

  return (
    <div data-testid="shop-home" data-hero={style.hero}>
      {hero}

      {style.productsFirst ? (<>{showcaseSection}{valuesSection}</>) : (<>{valuesSection}{showcaseSection}</>)}

      {/* Collezioni */}
      {homeCollections.length > 0 && (
        <Section
          title="Le collezioni"
          className="!pt-0"
          action={homeCollections.length > 3 ? <Link to={paths.page('collezioni')}><GhostLink>Tutte le collezioni <ArrowRight className="w-4 h-4" /></GhostLink></Link> : null}
        >
          <div data-testid="shop-home-collections">
            {homeCollections.length === 1 ? (
              <Reveal><CollectionTile collection={homeCollections[0]} to={paths.page(`collezioni/${homeCollections[0].slug}`)} large /></Reveal>
            ) : (
              <div className={`grid grid-cols-1 gap-6 ${homeCollections.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
                {homeCollections.slice(0, 3).map((c, i) => (
                  <Reveal key={c.id} delay={i * 0.06}>
                    <CollectionTile collection={c} to={paths.page(`collezioni/${c.slug}`)} />
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Categorie (nel modello Catalogo sono già in testa) */}
      {categories.length > 1 && style.hero !== 'compact' && (
        <Section title="Esplora per categoria" className="!pt-0">
          <div className={`grid gap-4 ${categories.length >= 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-3'}`}>
            {categories.slice(0, 8).map((c, i) => (
              <Reveal key={c.name} delay={i * 0.05}>
                <Link
                  to={`${paths.page('prodotti')}?categoria=${encodeURIComponent(c.name)}`}
                  className={`group relative block aspect-[4/3] ${style.media} overflow-hidden bg-slate-200 ${style.id === 'contemporaneo' ? 'border-2 border-slate-900' : ''}`}
                  data-testid="shop-category-tile"
                >
                  {productImage(c.product, 700) && <img src={productImage(c.product, 700)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                  <span className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                  <span className={`absolute bottom-3 left-4 right-4 text-white ${centered ? 'text-center' : ''}`}>
                    <span className={`block ${style.heading} text-lg md:text-xl`}>{c.name}</span>
                    <span className="text-xs text-white/80">{c.count} {c.count === 1 ? 'creazione' : 'creazioni'}</span>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </Section>
      )}

      {/* La storia della bottega */}
      {hasAbout && (crafter.bio || crafter.workshop_story) && (
        <section className={`${style.band} overflow-hidden`} data-testid="shop-story">
          <div className={`max-w-6xl mx-auto px-4 py-16 md:py-24 grid gap-10 md:gap-16 items-center ${photos.length && !centered ? 'md:grid-cols-2' : ''}`}>
            {photos.length > 0 && !centered && (
              <Reveal className="relative">
                <div className={`aspect-[4/5] overflow-hidden bg-slate-100 ${style.media} ${style.id === 'contemporaneo' ? 'border-2 border-slate-900' : ''}`}>
                  <img src={img(photos[1] || photos[0], 1000)} alt="" className="w-full h-full object-cover" />
                </div>
                {photos[2] && ['cover', 'collage', 'organic'].includes(style.hero) && (
                  <div className={`hidden md:block absolute -bottom-8 -right-8 w-2/5 aspect-square overflow-hidden border-8 border-white shadow-xl ${style.hero === 'organic' ? 'rounded-full' : 'rounded-2xl'}`}>
                    <img src={img(photos[2], 500)} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </Reveal>
            )}
            <Reveal delay={0.1} className={centered ? 'text-center max-w-2xl mx-auto' : ''}>
              <p className="text-xs uppercase tracking-[0.25em] text-shop-primary font-semibold">{crafter.storefront_section_name || 'La bottega'}</p>
              {centered && <div className="w-12 h-px bg-shop-accent mx-auto mt-5" />}
              {crafter.storefront_motto && (
                <p className={`${style.heading} mt-4 text-3xl md:text-4xl text-slate-900 leading-snug`}>
                  {!centered && <Quote className="inline w-7 h-7 -mt-3 mr-1 text-shop-accent" />}{crafter.storefront_motto}
                </p>
              )}
              <p className="mt-6 text-slate-600 leading-relaxed line-clamp-[8] whitespace-pre-line">{crafter.bio || crafter.workshop_story}</p>
              {Array.isArray(crafter.materials) && crafter.materials.length > 0 && (
                <div className={`mt-5 flex flex-wrap gap-2 ${centered ? 'justify-center' : ''}`}>
                  {crafter.materials.slice(0, 8).map((m) => (
                    <span key={m} className={`px-3 py-1 ${style.btn === 'rounded-none' ? '' : 'rounded-full'} border border-shop-primary/30 text-shop-primary text-xs font-medium`}>{m}</span>
                  ))}
                </div>
              )}
              <p className={`mt-6 ${style.heading} text-lg text-slate-800`}>— {crafter.full_name || name}</p>
              <Link to={paths.page('laboratorio')} className="inline-block mt-6"><GhostLink>Scopri la bottega <ArrowRight className="w-4 h-4" /></GhostLink></Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* Video di presentazione */}
      {parseVideo(crafter.presentation_video) && (
        <Section title="Guarda la bottega al lavoro" className="!pb-4" data-testid="shop-home-video">
          <Reveal className="max-w-4xl mx-auto">
            <ShopVideo url={crafter.presentation_video} title={`Video di ${name}`} />
          </Reveal>
        </Section>
      )}

      {/* Scorci del laboratorio */}
      {labPhotos.length >= 3 && (
        <section className="py-12 md:py-16" data-testid="shop-lab-strip">
          <div className={`max-w-6xl mx-auto px-4 mb-6 flex items-end gap-4 ${centered ? 'flex-col items-center' : 'justify-between'}`}>
            <h2 className={`${style.heading} text-2xl md:text-3xl text-slate-900`}>Dal laboratorio</h2>
            {menuKeys.includes('portfolio') && portfolio.length > 0 && (
              <Link to={paths.page('portfolio')}><GhostLink>Portfolio <ArrowRight className="w-4 h-4" /></GhostLink></Link>
            )}
          </div>
          <div className="flex gap-4 overflow-x-auto scrollbar-hide px-4 md:px-[max(1rem,calc((100vw-72rem)/2+1rem))] pb-2 snap-x">
            {labPhotos.map((src, i) => (
              <div key={i} className={`snap-start shrink-0 w-64 md:w-80 aspect-[4/5] overflow-hidden bg-slate-100 ${style.media} ${style.id === 'contemporaneo' ? 'border-2 border-slate-900' : ''}`}>
                <img src={img(src, 700)} alt="" loading="lazy" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recensioni */}
      {menuKeys.includes('recensioni') && reviewStats.reviewCount > 0 && (
        <Section
          title="Dicono della bottega"
          action={(
            <Link to={paths.page('recensioni')} className="inline-flex items-center gap-2 text-slate-700 hover:text-shop-primary">
              <Star className="w-5 h-5 fill-shop-accent text-shop-accent" />
              <span className="font-semibold">{reviewStats.averageRating.toFixed(1)}</span>
              <span className="text-slate-500 text-sm">· {reviewStats.reviewCount} recensioni verificate</span>
            </Link>
          )}
        >
          {quotes.length > 0 && (
            <div className="grid md:grid-cols-3 gap-5" data-testid="shop-testimonials">
              {quotes.map((r, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <figure className={`h-full p-6 ${style.panel} ${centered ? 'text-center' : ''}`}>
                    <div className={`flex gap-0.5 ${centered ? 'justify-center' : ''}`}>
                      {Array.from({ length: 5 }).map((_, k) => (
                        <Star key={k} className={`w-4 h-4 ${k < r.rating ? 'fill-shop-accent text-shop-accent' : 'text-slate-200'}`} />
                      ))}
                    </div>
                    <blockquote className={`mt-3 text-slate-700 leading-relaxed line-clamp-5 ${['editorial', 'boutique'].includes(style.hero) ? `${style.heading} text-lg` : ''}`}>“{r.comment.trim()}”</blockquote>
                    <figcaption className="mt-4 text-sm text-slate-500">{shortName(r.client?.full_name)} · acquisto verificato</figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Vieni in bottega: solo con visite attive e luogo pubblico */}
      {shop.branding.contact && shop.branding.contact.visits !== 'none' && (
        <Section title="Vieni in bottega" data-testid="shop-home-visit">
          <Reveal><ShopLocation compact /></Reveal>
        </Section>
      )}

      {/* Su misura */}
      {hasCustom && (
        <section className="max-w-6xl mx-auto px-4 py-12">
          <Reveal>
            <div className={`relative overflow-hidden bg-shop-primary-dark text-white ${style.btn === 'rounded-none' ? '' : style.id === 'naturale' ? 'rounded-[2.5rem]' : 'rounded-3xl'} ${style.id === 'contemporaneo' ? 'border-2 border-slate-900 shadow-[8px_8px_0_0_rgb(15,23,42)]' : ''}`}>
              {(photos[2] || photos[0]) && (
                <img src={img(photos[2] || photos[0], 1600)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
              )}
              <div className={`relative px-6 py-12 md:px-14 md:py-16 flex flex-col md:flex-row md:items-center justify-between gap-8 ${centered ? 'md:flex-col text-center items-center' : ''}`}>
                <div className="max-w-xl">
                  <h2 className={`${style.heading} text-3xl md:text-4xl`}>Hai un'idea tutta tua?</h2>
                  <p className="mt-3 text-white/85">Raccontala a {name}: riceverai una proposta con prezzo e tempi, senza impegno. Paghi solo se accetti.</p>
                </div>
                <PrimaryButton onClick={openRequest} className="bg-white !text-slate-900 hover:bg-white/90 shrink-0">
                  <Sparkles className="w-4 h-4" /> Richiedi su misura
                </PrimaryButton>
              </div>
            </div>
          </Reveal>
        </section>
      )}
    </div>
  );
}
